import { useEffect, useRef } from 'react'
import {
  postPresenceHeartbeat,
  postAnonPresenceHeartbeat,
  getAnonSessionId,
} from '../services/presence.api'

const HEARTBEAT_MS = 60_000

/**
 * Presencia:
 * - Con personaje activo → heartbeat autenticado
 * - Sin personaje (invitado o logueado sin ficha) → heartbeat anónimo
 * Solo mientras la pestaña está visible.
 */
export default function usePresenceHeartbeat({ user, character, enabled = true }) {
  const characterId = character?.characterId ?? null
  const userId = user?.userId ?? null
  const timerRef = useRef(null)

  useEffect(() => {
    if (!enabled) return undefined

    const useCharacter = !!(userId && characterId)
    let cancelled = false

    const send = async () => {
      if (cancelled) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      try {
        if (useCharacter) {
          await postPresenceHeartbeat({ characterId })
        } else {
          await postAnonPresenceHeartbeat({ sessionId: getAnonSessionId() })
        }
      } catch {
        // Silencioso
      }
    }

    const startInterval = () => {
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = setInterval(send, HEARTBEAT_MS)
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        send()
        startInterval()
      } else if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }

    send()
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      startInterval()
    }

    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [enabled, userId, characterId])
}
